import type {
  ArchetypeId,
  AxisId,
  SelvesInput,
  Trait,
  LifeChoice,
  WorldDetail,
  UniverseCondition,
  Dossier,
  ResolvedInput,
} from "./types";
import type { UniverseConfig, UniverseProfile } from "@/lib/universe";
import { generateDivergent } from "./narrative/divergent";
import { generateAscendant } from "./narrative/ascendant";
import { generateStrange } from "./narrative/strange";
import { generateUnexpected } from "./narrative/unexpected";

export interface NarrativeContext {
  archetype: ArchetypeId;
  input: SelvesInput; // raw visitor input
  name: string; // display name: nickname or "Traveler"
  variant: number;
  seed: number;
  rng: () => number; // main stream, mulberry32(seed ^ 0xdeadbeef ^ attempt)
  stream: (label: string) => () => number; // independent seeded stream: mulberry32(fnv1a(`${seed}:${attempt}:${label}`))
  attempt: number; // 0 normally; index.ts retries with 1..6 when collision detected
  resolved: ResolvedInput; // USE THIS for anything shaped by answers
  axes: Record<AxisId, number>;
  condition: UniverseCondition;
  universe: {
    name: string;
    code: string;
    config: UniverseConfig;
    profile: UniverseProfile;
  };
  sharedUniverse: boolean;
}

export interface NarrativeResult {
  name: string;
  title: string;
  tagline: string;
  occupation: string;
  traits: Trait[]; // exactly 4
  story: { origin: string; turningPoint: string; today: string };
  choices: LifeChoice[]; // exactly 3
  worldDetails: WorldDetail[]; // 4..6
  universeDescription: string;
  dossier: Dossier;
}

export const PREMISES: Record<ArchetypeId, string> = {
  divergent: "Your physics is our physics; only one early decision went the other way.",
  ascendant: "An advanced future civilization that has mastered energy, information, and deep-time stewardship.",
  strange: "A world of non-standard physics and alien topology, lived as ordinary everyday life.",
  unexpected: "An identity that playfully inverts your assumptions, revealing an eccentric, joyful alternate path.",
};

export function buildNarrative(ctx: NarrativeContext): NarrativeResult {
  switch (ctx.archetype) {
    case "divergent":
      return generateDivergent(ctx) as unknown as NarrativeResult;
    case "ascendant":
      return generateAscendant(ctx) as unknown as NarrativeResult;
    case "strange":
      return generateStrange(ctx) as unknown as NarrativeResult;
    case "unexpected":
      return generateUnexpected(ctx) as unknown as NarrativeResult;
  }
}
